/**
 * Lighting - Represents a Lighting element.
 * Extends Electric and contains all domain classes related to lighting infrastructure.
 * This is a composite class that aggregates all lighting-related domain objects.
 */
import { Electric } from "./Electric";

// Contracts domain
import { DOTPayItem } from "../contracts/DOTPayItem";

// Common domain
import { General } from "../common/General";
import { Extended } from "../common/Extended";

// Utilities domain
import { DrainageUtilities } from "../utilities/DrainageUtilities";
import { UtilityFootprint } from "../utilities/UtilityFootprint";
import { UtilityQuality } from "../utilities";
import { UtilityData } from "../utilities";

import { Feature } from "../utilities/Feature";
import { GeometryPoints } from "../utilities/GeometryPoints";
import { TriMesh } from "../utilities/Tmesh";
import type { IfcCurvePrimitive } from "../utilities/CurvePrimitives";
import { LinearReference } from "../utilities/LinearReference";
import { Coordinates } from "../utilities/Coordinates";
import { LatitudeLongitude } from "../utilities/LatitudeLongitude";
import { Origin } from "../common/Origin";

// Assets domain
import { Asset } from "../assets/Asset";

export class Lighting extends Electric {
  // Location Data
  Origin: Origin;

  // Mesh Data
  Meshes?: TriMesh[];
  Curves?: IfcCurvePrimitive[];

  // Contracts domain properties
  DOTPayItem1: DOTPayItem;
  DOTPayItem2: DOTPayItem;
  DOTPayItems: DOTPayItem[];

  // Common domain properties
  General: General;
  Extended: Extended;

  // Utilities domain properties
  DrainageUtilities: DrainageUtilities;
  UtilityFootprint: UtilityFootprint;
  UtilityQuality: UtilityQuality;
  UtilityData: UtilityData;

  Feature: Feature;
  GeometryPoints: GeometryPoints;
  LinearReference: LinearReference;
  Coordinates: Coordinates;
  LatitudeLongitude: LatitudeLongitude;

  /**
   * Creates a new Lighting instance
   * @param data - Object containing lighting element properties and all domain objects
   */
  constructor(data: {
    // Element base properties
    Model: string;
    LastMod: Date;
    CodeSpec: string;
    CodeScope: string;
    CodeValue: string;
    UserLabel: string;
    Parent: string;
    FederationGUID: string;
    JSONProperties: string;

    // Domain objects
    Origin?: Origin;
    Meshes?: TriMesh[]; // <-- FIXED (was TriMesh)
    Curves?: IfcCurvePrimitive[];
    DOTPayItem1?: DOTPayItem;
    DOTPayItem2?: DOTPayItem;
    DOTPayItems?: DOTPayItem[];
    General?: General;
    Extended?: Extended;
    DrainageUtilities?: DrainageUtilities;
    UtilityFootPrint?: UtilityFootprint;
    UtilityQuality?: UtilityQuality;
    UtilityData?: UtilityData;
    Feature?: Feature;
    GeometryPoints?: GeometryPoints;
    LinearReference?: LinearReference;
    Coordinates?: Coordinates;
    LatitudeLongitude?: LatitudeLongitude;
    Asset?: Asset;
  }) {
    super({
      Model: data.Model,
      LastMod: data.LastMod,
      CodeSpec: data.CodeSpec,
      CodeScope: data.CodeScope,
      CodeValue: data.CodeValue,
      UserLabel: data.UserLabel,
      Parent: data.Parent,
      FederationGUID: data.FederationGUID,
      JSONProperties: data.JSONProperties,
    });

    // Initialize domain objects with defaults if not provided
    this.Origin = data.Origin ?? Origin.fromObject({});
    this.Meshes = data.Meshes; // pass-through
    this.Curves = data.Curves;

    this.DOTPayItem1 = data.DOTPayItem1 ?? DOTPayItem.fromObject({});
    this.DOTPayItem2 = data.DOTPayItem2 ?? DOTPayItem.fromObject({});
    this.DOTPayItems = data.DOTPayItems ?? [];
    this.General = data.General ?? General.fromObject({});
    this.Extended = data.Extended ?? Extended.fromObject({});
    this.DrainageUtilities = data.DrainageUtilities
      ? DrainageUtilities.fromObject(data.DrainageUtilities)
      : DrainageUtilities.fromObject({});
    this.UtilityFootprint = data.UtilityFootPrint
      ? UtilityFootprint.fromObject(data.UtilityFootPrint)
      : UtilityFootprint.fromObject({});
    this.UtilityQuality = data.UtilityQuality
      ? UtilityQuality.fromObject(data.UtilityQuality)
      : UtilityQuality.fromObject({});
    this.UtilityData = data.UtilityData
      ? UtilityData.fromObject(data.UtilityData)
      : UtilityData.fromObject({});
    this.Feature = data.Feature ?? Feature.fromObject({});
    this.GeometryPoints = data.GeometryPoints
      ? GeometryPoints.fromObject(data.GeometryPoints)
      : GeometryPoints.fromObject({});
    this.LinearReference = data.LinearReference
      ? LinearReference.fromObject(data.LinearReference)
      : LinearReference.fromObject({});
    this.Coordinates = data.Coordinates
      ? Coordinates.fromObject(data.Coordinates)
      : Coordinates.fromObject({});
    this.LatitudeLongitude = data.LatitudeLongitude
      ? LatitudeLongitude.fromObject(data.LatitudeLongitude)
      : LatitudeLongitude.fromObject({});
  }

  /**
   * Creates a Lighting from a plain object (useful for deserialization)
   * @param obj - Plain object with lighting element data
   * @returns New Lighting instance
   */
  static fromObject(obj: Partial<Lighting>): Lighting {
    let lastMod: Date;
    if (obj.LastMod instanceof Date) {
      lastMod = obj.LastMod;
    } else if (typeof obj.LastMod === "string") {
      lastMod = new Date(obj.LastMod);
    } else {
      lastMod = new Date();
    }

    return new Lighting({
      Model: obj.Model ?? "",
      LastMod: lastMod,
      CodeSpec: obj.CodeSpec ?? "",
      CodeScope: obj.CodeScope ?? "",
      CodeValue: obj.CodeValue ?? "",
      UserLabel: obj.UserLabel ?? "",
      Parent: obj.Parent ?? "",
      FederationGUID: obj.FederationGUID ?? "",
      JSONProperties: obj.JSONProperties ?? "",

      Origin: obj.Origin ? Origin.fromObject(obj.Origin) : undefined,

      // <-- ADDED
      Meshes: Array.isArray(obj.Meshes) ? obj.Meshes : undefined,
      Curves: Array.isArray((obj as any).Curves) ? ((obj as any).Curves as IfcCurvePrimitive[]) : undefined,

      DOTPayItem1: obj.DOTPayItem1 ? DOTPayItem.fromObject(obj.DOTPayItem1) : undefined,
      DOTPayItem2: obj.DOTPayItem2 ? DOTPayItem.fromObject(obj.DOTPayItem2) : undefined,
      DOTPayItems: Array.isArray(obj.DOTPayItems)
        ? obj.DOTPayItems.map((p) => DOTPayItem.fromObject(p as any))
        : undefined,
      General: obj.General ? General.fromObject(obj.General) : undefined,
      Extended: obj.Extended ? Extended.fromObject(obj.Extended) : undefined,
      DrainageUtilities: obj.DrainageUtilities
        ? DrainageUtilities.fromObject(obj.DrainageUtilities)
        : undefined,
      UtilityFootPrint: obj.UtilityFootprint
        ? UtilityFootprint.fromObject(obj.UtilityFootprint)
        : undefined,
      UtilityQuality: obj.UtilityQuality
        ? UtilityQuality.fromObject(obj.UtilityQuality)
        : undefined,
      UtilityData: obj.UtilityData ? UtilityData.fromObject(obj.UtilityData) : undefined,
      Feature: obj.Feature ? Feature.fromObject(obj.Feature) : undefined,
      GeometryPoints: obj.GeometryPoints ? GeometryPoints.fromObject(obj.GeometryPoints) : undefined,
      LinearReference: obj.LinearReference ? LinearReference.fromObject(obj.LinearReference) : undefined,
      Coordinates: obj.Coordinates ? Coordinates.fromObject(obj.Coordinates) : undefined,
      LatitudeLongitude: obj.LatitudeLongitude
        ? LatitudeLongitude.fromObject(obj.LatitudeLongitude)
        : undefined,
    });
  }

  /**
   * Converts the Lighting to a plain object (useful for serialization)
   * @returns Plain object representation
   */
  toObject(): Record<string, unknown> {
    return {
      ...super.toObject(),
      Origin: this.Origin.toObject(),

      // <-- ADDED (pass-through; TriMesh is already plain-data shaped)
      Meshes: this.Meshes,
      Curves: this.Curves,

      DOTPayItem1: this.DOTPayItem1.toObject(),
      DOTPayItem2: this.DOTPayItem2.toObject(),
      DOTPayItems: this.DOTPayItems.map((p) => p.toObject()),
      General: this.General.toObject(),
      Extended: this.Extended.toObject(),
      DrainageUtilities: this.DrainageUtilities.toObject(),
      UtilityFootprint: this.UtilityFootprint.toObject(),
      UtilityQuality: this.UtilityQuality.toObject(),
      UtilityData: this.UtilityData.toObject(),
      Feature: this.Feature.toObject(),
      GeometryPoints: this.GeometryPoints.toObject(),
      LinearReference: this.LinearReference.toObject(),
      Coordinates: this.Coordinates.toObject(),
      LatitudeLongitude: this.LatitudeLongitude.toObject(),
    };
  }
}

